import React from 'react';
import { focusRing } from '../../Themes/base/focusRing';
import { getBaseCustomProps, getFormCustomProps } from '../../Utilities/types/SharedProps';
import { Button } from '../Button';
import { MIcon } from '../Icon';
import { Tooltip } from '../Tooltip';
import {
	InputEpoCustoms,
	InputBaseFontStyle,
	InputBaseStyle,
	IconBeforeInputStyles,
	InputDisabledStyle,
	ForceRemoveBorders,
	IconAfterInputStyles,
	InputSpacing,
	InputColorStyle,
} from './styles';
import { IDummyProps } from './types';
import { CloseIconComponent } from '../../Utilities/utils/Theming';
import * as meta from '../../meta-data.json';

export const overrides = (props: IDummyProps, dataProps: any, dataAria: any, noDataAriaProps: any) => {
	const {
		clearOnEscape,
		autoComplete,
		autoFocus,
		onFocus,
		onBlur,
		inputRef,
		className,
		error,
		positive,
		iconBefore,
		type,
		clearable,
		endEnhancer,
		startEnhancer,
		warning,
		...rest
	} = noDataAriaProps;
	delete rest['root-props'];
	delete rest['input-props'];

	return {
		Root: {
			style: ({ $theme, $isFocused, $error, $positive }: any) => {
				return {
					...InputBaseFontStyle($theme, rest),
					...InputBaseStyle($theme, props, $isFocused),
					// Note: for some reason warning is not passed in this function
					// (maybe it comes from form control)
					...InputEpoCustoms($theme, $isFocused, $error, $positive, warning || false, props),
				};
			},
			props: {
				...getBaseCustomProps(props),
				...dataProps,
				className: props.className,
				'data-epo': `epo-input-${meta.version}`,
				part: `epo-input-${meta.version}`,
				key: props.key,
			},
		},
		InputContainer: {
			style: ({ $theme }: any) => {
				return {
					...ForceRemoveBorders,
					backgroundColor: 'transparent',
					color: 'inherit',
				};
			},
		},
		Input: {
			style: ({ $theme }: any) => {
				return {
					...InputBaseFontStyle($theme, rest),
					...InputSpacing($theme, props),
					...InputDisabledStyle($theme, props),
					...InputColorStyle($theme, props),
					color: 'inherit',
					backgroundColor: 'transparent',
					height: `var(--epods--input-${rest.size}-height)`,
				};
			},
			props: {
				...getFormCustomProps(props),
				...dataAria,
				...rest,
				autoComplete: 'off',
				tabIndex: props.tabIndex || 0,
				name: props.name,
				readOnly: props.readOnly ? 'readonly' : false,
			},
		},
		ClearIcon: {
			component: ({
				$isFocused,
				$isFocusVisible,
				$disabled,
				$size,
				$error,
				$positive,
				$required,
				$adjoined,
				...args
			}: any) => <CloseIconComponent tooltipContent="Clear" {...args} />,
		},
		MaskToggleButton: {
			component: (args: any) => {
				return (
					<Tooltip content="Show password">
						<span>
							<Button {...args} theme="tertiary-phantom" aria-label="Show password" />
						</span>
					</Tooltip>
				);
			},
			style: () => {
				return {
					// Todo: not working :(
					paddingLeft: '9px',
					paddingRight: '9px',
					':focus': focusRing,
				};
			},
		},
		MaskToggleShowIcon: {
			component: (props: any) => (
				<MIcon icon="visibility" size="small" color="var(--epods--color-text-primary)" {...props} />
			),
		},
		MaskToggleHideIcon: {
			component: (props: any) => (
				<MIcon icon="visibility" size="small" color="var(--epods--color-text-primary)" {...props} />
			),
		},
		ClearIconContainer: {
			style: ({ $theme }: any) => {
				return {
					paddingRight: props.iconAfter ? '0' : '12px', // Trick to hide the original icon
				};
			},
		},
		EndEnhancer: {
			style: ({ $theme }: any) => {
				return {
					...IconAfterInputStyles($theme),
					backgroundColor: 'transparent',
				};
			},
		},
		StartEnhancer: {
			style: ({ $theme }: any) => {
				return {
					...IconBeforeInputStyles($theme),
					backgroundColor: 'transparent',
				};
			},
		},
	};
};
